import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/money'

import { useAdminMutation, useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import { TableCustomizationCard } from '../components/TableCustomizationCard'
import { TableRequestFollowUp } from '../components/TableRequestFollowUp'
import { useAdminContext } from '../session'
import type { Page } from '../useCollection'
import type { TableRequest, TableTypeOption } from '../types'

/**
 * Les demandes de table : les tables personnalisées (« Personnaliser cette
 * table » sur le site), et les demandes de l'ancien formulaire libre « sur
 * mesure », retiré du site mais dont les demandes reçues se traitent ici.
 *
 * Chaque ligne est quelqu'un qui attend un appel — c'est ce qui gouverne
 * l'écran. Les demandes non traitées passent devant, et la plus ancienne
 * d'abord : celui qui attend depuis une semaine passe avant celui d'hier.
 *
 * Annoncer un prix **envoie** le message. Un devis qui reste dans une base ne
 * sert à personne, et un écran qui laisse croire le contraire fait perdre des
 * clients sans que personne ne comprenne pourquoi.
 */
const TONES: Record<string, 'neutral' | 'champagne' | 'success' | 'ember'> = {
  new: 'champagne',
  quoted: 'neutral',
  accepted: 'success',
  declined: 'ember',
  cancelled: 'neutral',
}

export function AdminTableRequestsPage() {
  const { t, i18n } = useTranslation()
  const { eventId } = useAdminContext()

  useEffect(() => {
    document.title = t('admin.section.tableRequests')
  }, [t])

  const base = `/admin/events/${eventId}/table-requests`
  const requests = useAdminQuery<Page<TableRequest>>(
    ['admin-table-requests', eventId], `${base}?page_size=200`, eventId,
  )

  const invalidate = [['admin-table-requests', eventId]]
  const tableTypes = useAdminQuery<TableTypeOption[]>(
    ['admin-table-request-types', eventId], `${base}/table-types`, eventId,
  )
  const quote = useAdminMutation<
    TableRequest,
    { id: string; price_minor: number; message: string | null; includes_admission: boolean }
  >('post', (variables) => `${base}/${variables.id}/quote`, eventId, invalidate)
  const accept = useAdminMutation<
    TableRequest,
    { id: string; table_type_id: string | null; name: string | null }
  >(
    'post', (variables) => `${base}/${variables.id}/accept`, eventId, invalidate,
  )
  const decline = useAdminMutation<TableRequest, { id: string; reason: string }>(
    'post', (variables) => `${base}/${variables.id}/decline`, eventId, invalidate,
  )
  const remove = useAdminMutation<{ message: string }, { id: string }>(
    'delete', (variables) => `${base}/${variables.id}`, eventId, invalidate,
  )

  const busy =
    quote.isPending || accept.isPending || decline.isPending || remove.isPending
  const error =
    quote.error ?? accept.error ?? decline.error ?? remove.error

  const rows = requests.data?.items ?? []
  const waiting = rows.filter((row) => row.status === 'new').length

  return (
    <>
      <AdminHeader
        title={t('admin.section.tableRequests')}
        description={t('admin.tableRequests.description')}
      />

      {waiting > 0 ? (
        <p className="mb-5 text-sm text-champagne-300">
          {t('admin.tableRequests.waiting', { count: waiting })}
        </p>
      ) : null}

      {error ? <p className="mb-4 text-sm text-ember-300">{error.message}</p> : null}
      {requests.isPending ? <Skeleton className="h-64 w-full" /> : null}

      {rows.length === 0 && !requests.isPending ? (
        <p className="text-sm text-content-muted">{t('admin.tableRequests.empty')}</p>
      ) : null}

      <div className="space-y-4">
        {rows.map((row) =>
          row.kind === 'customization' ? (
            <TableCustomizationCard
              key={row.id}
              request={row}
              busy={busy}
              onDecline={(reason) => decline.mutate({ id: row.id, reason })}
              onRemove={() => remove.mutate({ id: row.id })}
            />
          ) : (
          <RequestCard
            key={row.id}
            request={row}
            locale={i18n.language}
            busy={busy}
            tableTypes={tableTypes.data ?? []}
            onQuote={(price_minor, message, includes_admission) =>
              quote.mutate({ id: row.id, price_minor, message, includes_admission })
            }
            onAccept={(table_type_id, name) => accept.mutate({ id: row.id, table_type_id, name })}
            onDecline={(reason) => decline.mutate({ id: row.id, reason })}
            onRemove={() => remove.mutate({ id: row.id })}
          />
          ),
        )}
      </div>
    </>
  )
}

function RequestCard({
  request,
  locale,
  busy,
  tableTypes,
  onQuote,
  onAccept,
  onDecline,
  onRemove,
}: {
  request: TableRequest
  locale: string
  busy: boolean
  tableTypes: TableTypeOption[]
  onQuote: (priceMinor: number, message: string | null, includesAdmission: boolean) => void
  onAccept: (tableTypeId: string | null, name: string | null) => void
  onDecline: (reason: string) => void
  onRemove: () => void
}) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<'idle' | 'quote' | 'decline' | 'accept' | 'remove'>(
    'idle',
  )
  const [price, setPrice] = useState(
    request.quoted_price_minor ? (request.quoted_price_minor / 100).toFixed(2) : '',
  )
  const [message, setMessage] = useState(request.quote_message ?? '')
  const [admission, setAdmission] = useState(request.includes_admission)
  const [reason, setReason] = useState('')
  // Le type « Sur mesure » d'office ; à défaut, le premier qui accueille le
  // groupe. Le nom du groupe est facultatif.
  const fitting = tableTypes.filter(
    (option) =>
      option.capacity_min <= request.party_size && request.party_size <= option.capacity_max,
  )
  const suggested =
    tableTypes.find((option) => option.suggested) ?? fitting[0] ?? tableTypes[0] ?? null
  const [tableTypeId, setTableTypeId] = useState<string>('')
  const chosenType = tableTypeId || suggested?.id || ''
  const chosen = tableTypes.find((option) => option.id === chosenType) ?? null
  const outOfRange =
    chosen !== null &&
    (request.party_size < chosen.capacity_min || request.party_size > chosen.capacity_max)
  const [groupName, setGroupName] = useState('')

  const money = (minor: number) =>
    formatMoney({ amount_minor: minor, currency: request.currency }, locale)

  return (
    <Card className={cn(request.status === 'new' && 'border-l-4 border-l-champagne-400')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-content-primary">
            {request.full_name}
            <span className="ml-2 text-sm text-content-muted">
              {t('admin.tableRequests.people', { count: request.party_size })}
            </span>
          </p>
          <p className="mt-0.5 text-caption text-content-muted">
            {request.email}
            {request.phone ? ` · ${request.phone}` : ''} ·{' '}
            {new Date(request.created_at).toLocaleDateString(locale)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* L'ancien formulaire libre est retiré du site : ses demandes se
              traitent encore ici, et l'écran le dit. */}
          <Badge tone="neutral">{t('admin.tableRequests.legacyBadge')}</Badge>
          <Badge tone={TONES[request.status] ?? 'neutral'}>
            {t(`admin.tableRequests.statuses.${request.status}`)}
          </Badge>
        </div>
      </div>

      {request.drinks ? (
        <p className="mt-3 whitespace-pre-line text-sm text-content-secondary">
          {request.drinks}
        </p>
      ) : null}
      {request.notes ? (
        <p className="mt-2 whitespace-pre-line text-caption text-content-muted">
          {request.notes}
        </p>
      ) : null}

      {request.quoted_price_minor !== null ? (
        <p className="mt-3 text-sm text-content-secondary">
          {t('admin.tableRequests.quotedAt', {
            price: money(request.quoted_price_minor),
            who: request.quoted_by_label ?? '—',
          })}
        </p>
      ) : null}
      {request.quoted_price_minor !== null ? (
        <p className="mt-1 text-caption text-content-muted">
          {request.includes_admission
            ? t('admin.tableRequests.admissionIncluded')
            : t('admin.tableRequests.admissionExcluded')}
        </p>
      ) : null}
      {request.decline_reason ? (
        <p className="mt-2 text-sm text-ember-300">« {request.decline_reason} »</p>
      ) : null}
      {/* L'accord a créé une commande : c'est là que le règlement se suit et
          se pointe. Le lien ouvre « Commandes » filtré sur son numéro. */}
      {request.order_number ? (
        <p className="mt-2 text-sm text-content-secondary">
          {t('admin.tableRequests.order')}{' '}
          <Link
            to={`../commandes?q=${encodeURIComponent(request.order_number)}`}
            relative="path"
            className="tabular-nums text-champagne-300 underline"
          >
            {request.order_number}
          </Link>
        </p>
      ) : null}

      {mode === 'accept' ? (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-content-secondary">
            {t('admin.tableRequests.acceptConfirm', {
              price: money(request.quoted_price_minor ?? 0),
              email: request.email,
            })}
          </p>
          {tableTypes.length === 0 ? (
            <p className="text-sm text-state-warning">{t('admin.tableRequests.noTableType')}</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-caption text-content-muted">
                {t('admin.tableRequests.tableType')}
                <select
                  value={chosenType}
                  onChange={(event) => setTableTypeId(event.target.value)}
                  className={INPUT}
                >
                  {tableTypes.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label} ({option.capacity_min}–{option.capacity_max})
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-caption text-content-muted">
                {t('admin.tableRequests.groupName')}
                <input
                  value={groupName}
                  maxLength={120}
                  placeholder={t('admin.tableRequests.groupNamePlaceholder')}
                  onChange={(event) => setGroupName(event.target.value)}
                  className={INPUT}
                />
              </label>
            </div>
          )}
          {outOfRange && chosen ? (
            <p className="text-sm text-state-warning">
              {t('admin.tableRequests.typeCapacity', {
                min: chosen.capacity_min,
                max: chosen.capacity_max,
                count: request.party_size,
              })}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={busy || !chosen || outOfRange}
              onClick={() => {
                onAccept(chosenType || null, groupName.trim() || null)
                setMode('idle')
              }}
            >
              {t('admin.tableRequests.acceptYes')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setMode('idle')}>
              {t('admin.project.cancel')}
            </Button>
          </div>
        </div>
      ) : mode === 'remove' ? (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-sm text-content-secondary">
            {request.order_number
              ? t('admin.tableRequests.removeConfirmOrder', { order: request.order_number })
              : t('admin.tableRequests.removeConfirm')}
          </span>
          <Button
            variant="champagne"
            size="sm"
            disabled={busy}
            onClick={() => {
              onRemove()
              setMode('idle')
            }}
          >
            {t('admin.project.deleteYes')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setMode('idle')}>
            {t('admin.project.cancel')}
          </Button>
        </div>
      ) : mode === 'quote' ? (
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="text-caption text-content-muted">
            {t('admin.tableRequests.price', { currency: request.currency })}
            <input
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              className={cn(INPUT, 'w-32 tabular-nums')}
            />
          </label>
          <label className="min-w-48 flex-1 text-caption text-content-muted">
            {t('admin.tableRequests.message')}
            <input
              value={message}
              placeholder={t('admin.tableRequests.messagePlaceholder')}
              onChange={(event) => setMessage(event.target.value)}
              className={INPUT}
            />
          </label>
          <Button
            size="sm"
            disabled={busy || !price.trim()}
            onClick={() => {
              const value = Number.parseFloat(price.replace(',', '.'))
              if (!Number.isFinite(value) || value < 0) return
              onQuote(Math.round(value * 100), message.trim() || null, admission)
              setMode('idle')
            }}
          >
            {t('admin.tableRequests.sendQuote')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setMode('idle')}>
            {t('admin.project.cancel')}
          </Button>
          <label className="flex w-full items-start gap-2 text-sm text-content-secondary">
            <input
              type="checkbox"
              checked={admission}
              onChange={() => setAdmission((value) => !value)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--ember-500)]"
            />
            <span>{t('admin.tableRequests.includesAdmission')}</span>
          </label>
        </div>
      ) : mode === 'decline' ? (
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="min-w-48 flex-1 text-caption text-content-muted">
            {t('admin.tableRequests.declineReason')}
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className={INPUT}
            />
          </label>
          <Button
            size="sm"
            disabled={busy || reason.trim().length < 3}
            onClick={() => {
              onDecline(reason.trim())
              setMode('idle')
            }}
          >
            {t('admin.tableRequests.confirmDecline')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setMode('idle')}>
            {t('admin.project.cancel')}
          </Button>
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap gap-2">
          {/* Une demande acceptée a créé sa commande : le serveur refuse de la
              rechiffrer ou de la décliner, on ne le propose donc plus. Une
              demande refusée ou annulée est close : ni devis ni refus. */}
          {!['declined', 'cancelled', 'accepted'].includes(request.status) ? (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => setMode('quote')}>
              {request.quoted_price_minor === null
                ? t('admin.tableRequests.quote')
                : t('admin.tableRequests.requote')}
            </Button>
          ) : null}
          {request.status === 'quoted' ? (
            <Button size="sm" disabled={busy} onClick={() => setMode('accept')}>
              {t('admin.tableRequests.accept')}
            </Button>
          ) : null}
          {!['declined', 'cancelled', 'accepted'].includes(request.status) ? (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setMode('decline')}>
              {t('admin.tableRequests.decline')}
            </Button>
          ) : null}
          {/* Retirer efface la demande pour de bon : une confirmation, comme
              pour toute suppression. */}
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => setMode('remove')}>
            {t('admin.tableRequests.remove')}
          </Button>

        </div>
      )}

      {request.status !== 'declined' && request.status !== 'cancelled' ? (
        <TableRequestFollowUp request={request} />
      ) : null}

      {/* Dit sur chaque carte : annoncer un prix envoie vraiment le message. */}
      {mode === 'quote' ? (
        <p className="mt-2 text-caption text-content-muted">
          {t('admin.tableRequests.sendsEmail')}
        </p>
      ) : null}
    </Card>
  )
}

const INPUT =
  'mt-1 h-10 w-full rounded-md border border-line bg-bg-base px-3 text-sm text-content-primary'
